import apiClient from './client'
import type {
  ApiLabRequest,
  ApiLabResponse,
  AuthTokenResponse,
  RefreshTokenResponse,
  AuthUser,
  BillingCheckoutResponse,
  BillingPortalResponse,
  BillingStatusResponse,
} from '../types'

// Maps frontend tool name → { backend route segment, HTTP method }
const TOOL_ROUTE_MAP: Record<string, { path: string; method: 'get' | 'post' }> = {
  'json-formatter':  { path: 'json-formatter',  method: 'post' },
  'jwt-decoder':     { path: 'jwt-decoder',     method: 'post' },
  'uuid-generator':  { path: 'uuid',            method: 'get'  },
  'regex-tester':    { path: 'regex-tester',    method: 'post' },
  'base64':          { path: 'base64',          method: 'post' },
  'hmac-generator':  { path: 'hmac-generator',  method: 'post' },
  'jwt-generator':   { path: 'jwt-generator',   method: 'post' },
  'qr-generator':    { path: 'qr-generator',    method: 'post' },
  'toml-json':       { path: 'toml-json',       method: 'post' },
  // P7 — network tools (JSON body, standard pattern)
  'dns-lookup':      { path: 'dns-lookup',      method: 'post' },
  'ssl-inspector':   { path: 'ssl-inspector',   method: 'post' },
  'http-headers':    { path: 'http-headers',    method: 'post' },
  'ip-info':         { path: 'ip-info',         method: 'post' },
  'url-metadata':    { path: 'url-metadata',    method: 'post' },
  // P8B — server-side sandboxed runners
  'js-runner':       { path: 'js-runner',       method: 'post' },
}

export async function callTool(toolName: string, payload: unknown): Promise<unknown> {
  const route = TOOL_ROUTE_MAP[toolName] ?? { path: toolName, method: 'post' as const }

  if (route.method === 'get') {
    const response = await apiClient.get<unknown>(
      `/api/v1/tools/${route.path}`,
      { params: payload as Record<string, unknown> },
    )
    return response.data
  }

  const response = await apiClient.post<unknown>(`/api/v1/tools/${route.path}`, payload)
  return response.data
}

export async function executeLabRequest(payload: ApiLabRequest): Promise<ApiLabResponse> {
  const response = await apiClient.post<ApiLabResponse>('/api/v1/lab/execute', payload)
  return response.data
}

// ── Auth ───────────────────────────────────────────────────────────────────────

/**
 * Persist or clear the Bearer token on the shared Axios instance.
 * Call this from AuthContext after login / refresh / logout.
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete apiClient.defaults.headers.common['Authorization']
  }
}

/** POST /api/v1/auth/register — create account, returns access token + user */
export async function authRegister(email: string, password: string): Promise<AuthTokenResponse> {
  const response = await apiClient.post<AuthTokenResponse>('/api/v1/auth/register', { email, password })
  return response.data
}

/** POST /api/v1/auth/login — authenticate, returns access token + user; sets httpOnly refresh cookie */
export async function authLogin(email: string, password: string): Promise<AuthTokenResponse> {
  const response = await apiClient.post<AuthTokenResponse>(
    '/api/v1/auth/login',
    { email, password },
    { withCredentials: true },
  )
  return response.data
}

/**
 * POST /api/v1/auth/refresh — exchange httpOnly refresh cookie for a new access token.
 * Rotates the refresh token (one-time use). Must NOT send a Bearer header.
 */
export async function authRefresh(): Promise<RefreshTokenResponse> {
  const response = await apiClient.post<RefreshTokenResponse>(
    '/api/v1/auth/refresh',
    {},
    { withCredentials: true },
  )
  return response.data
}

/** POST /api/v1/auth/logout — revoke refresh token in DB, clear cookie. Requires Bearer token. */
export async function authLogout(): Promise<void> {
  await apiClient.post('/api/v1/auth/logout', {}, { withCredentials: true })
}

/** GET /api/v1/auth/me — returns current user (id, email, plan, subscription). Requires Bearer token. */
export async function getAuthMe(): Promise<AuthUser> {
  const response = await apiClient.get<AuthUser>('/api/v1/auth/me')
  return response.data
}

// ── Billing ───────────────────────────────────────────────────────────────────

/** POST /api/v1/billing/checkout — create Stripe Checkout session. Returns redirect URL. */
export async function createCheckoutSession(priceId: string): Promise<BillingCheckoutResponse> {
  const response = await apiClient.post<BillingCheckoutResponse>('/api/v1/billing/checkout', { priceId })
  return response.data
}

/** GET /api/v1/billing/portal — create Stripe Customer Portal session. Returns redirect URL. */
export async function getBillingPortal(): Promise<BillingPortalResponse> {
  const response = await apiClient.get<BillingPortalResponse>('/api/v1/billing/portal')
  return response.data
}

/** GET /api/v1/billing/status — returns current plan and subscription period info. */
export async function getBillingStatus(): Promise<BillingStatusResponse> {
  const response = await apiClient.get<BillingStatusResponse>('/api/v1/billing/status')
  return response.data
}

// ── File upload tools ─────────────────────────────────────────────────────────

/** POST /api/v1/tools/[tool] with multipart/form-data; file field name = 'file' */
export async function uploadFileTool(toolPath: string, file: File): Promise<unknown> {
  const form = new FormData()
  form.append('file', file)
  const response = await apiClient.post<unknown>(`/api/v1/tools/${toolPath}`, form)
  return response.data
}

// ── Pro — Session History ─────────────────────────────────────────────────────

export interface HistoryEntry {
  id:            string
  toolId:        string
  toolName:      string
  inputPreview:  string | null
  outputPreview: string | null
  createdAt:     number   // Unix ms
}

export interface HistoryListResponse { entries: HistoryEntry[]; total: number }

export async function getHistory(page = 0): Promise<HistoryListResponse> {
  const response = await apiClient.get<HistoryListResponse>('/api/v1/history', { params: { page } })
  return response.data
}

export async function deleteHistoryEntry(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/history/${id}`)
}

export async function clearHistory(): Promise<void> {
  await apiClient.delete('/api/v1/history')
}

// ── Pro — Shareable Output URLs ───────────────────────────────────────────────

export interface ShareCreateResponse { token: string }
export interface SharedOutputResponse { toolId: string; toolName: string; output: string; createdAt: number }

export async function createShare(toolId: string, output: string): Promise<ShareCreateResponse> {
  const response = await apiClient.post<ShareCreateResponse>('/api/v1/share', { toolId, output })
  return response.data
}

export async function getSharedOutput(token: string): Promise<SharedOutputResponse> {
  const response = await apiClient.get<SharedOutputResponse>(`/api/v1/share/${token}`)
  return response.data
}

export async function deleteShare(token: string): Promise<void> {
  await apiClient.delete(`/api/v1/share/${token}`)
}

// ── Pro — Webhook Receiver/Tester ─────────────────────────────────────────────

export interface WebhookEndpoint { id: string; label: string; url: string; createdAt: number }
export interface WebhookDelivery {
  id:         string
  method:     string
  headers:    Record<string, string>
  body:       string
  receivedAt: number
}

export async function getWebhookEndpoints(): Promise<WebhookEndpoint[]> {
  const response = await apiClient.get<WebhookEndpoint[]>('/api/v1/webhooks')
  return response.data
}

export async function createWebhookEndpoint(label: string): Promise<WebhookEndpoint> {
  const response = await apiClient.post<WebhookEndpoint>('/api/v1/webhooks', { label })
  return response.data
}

export async function deleteWebhookEndpoint(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/webhooks/${id}`)
}

export async function getWebhookDeliveries(endpointId: string): Promise<WebhookDelivery[]> {
  const response = await apiClient.get<{ deliveries: WebhookDelivery[] }>(`/api/v1/webhooks/${endpointId}/deliveries`)
  return response.data.deliveries ?? []
}

// ── File upload tool with extra form params (P8A-02 Image Compressor) ─────────

/**
 * POST /api/v1/tools/[tool] with multipart/form-data.
 * Supports additional string/number params alongside the file field.
 */
export async function uploadFileToolWithParams(
  toolPath: string,
  file:     File,
  params:   Record<string, string | number>,
): Promise<unknown> {
  const form = new FormData()
  form.append('file', file)
  for (const [key, value] of Object.entries(params)) {
    form.append(key, String(value))
  }
  const response = await apiClient.post<unknown>(`/api/v1/tools/${toolPath}`, form)
  return response.data
}

// ── Pro — My Files (R2 cloud storage) ─────────────────────────────────────────

export interface UserFile {
  id:        string
  filename:  string
  sizeBytes: number
  mimeType:  string
  createdAt: number  // Unix ms
}

export interface FilesListResponse {
  files: UserFile[]
  quota: { usedBytes: number; totalBytes: number }
}

export async function listUserFiles(): Promise<FilesListResponse> {
  const response = await apiClient.get<FilesListResponse>('/api/v1/files')
  return response.data
}

export async function uploadUserFile(file: File): Promise<void> {
  // Route upload through backend — backend proxies to R2 (avoids CORS on localhost/production)
  const form = new FormData()
  form.append('file', file, file.name)
  await apiClient.post('/api/v1/files/upload', form)
}

export async function deleteUserFile(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/files/${id}`)
}

export async function getUserFileDownloadUrl(id: string): Promise<{ url: string }> {
  const response = await apiClient.post<{ downloadUrl: string }>(`/api/v1/files/${id}/download-url`)
  return { url: response.data.downloadUrl }
}

// ── Pro — API Keys ─────────────────────────────────────────────────────────────

export interface ApiKey {
  id:         string
  name:       string
  scopes:     string[]
  createdAt:  number   // Unix ms
  lastUsedAt: number | null
}

export interface CreateApiKeyResponse {
  id:        string
  name:      string
  scopes:    string[]
  createdAt: number
  plaintext: string   // shown only once
}

export async function listApiKeys(): Promise<ApiKey[]> {
  const response = await apiClient.get<ApiKey[]>('/api/v1/api-keys')
  return response.data
}

export async function createApiKey(name: string, scopes: string[]): Promise<CreateApiKeyResponse> {
  const response = await apiClient.post<CreateApiKeyResponse>('/api/v1/api-keys', { name, scopes })
  return response.data
}

export async function revokeApiKey(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/api-keys/${id}`)
}

// ── Feedback ───────────────────────────────────────────────────────────────────

export interface FeedbackRequest {
  name:      string
  email:     string
  message:   string
  category?: 'bug' | 'feature' | 'general'
}

/** POST /api/v1/feedback — public, no auth header */
export async function submitFeedback(body: FeedbackRequest): Promise<void> {
  await apiClient.post('/api/v1/feedback', body)
}
